import React from 'react';

import { events, messageBus } from '@schibsted-nmp/advertising-events';
import { debug, type LayoutType } from '@schibsted-nmp/advertising-shared';

import { useAdComponents } from './useAdComponents.js';
import { useSearchPositionInstruction } from './useSearchPositionInstruction.js';

export type AdPositionElements = readonly [
  adPositions: ReadonlyArray<number>,
  adElements: ReadonlyArray<React.ReactNode>
];

export namespace AdPositionElements {
  export function use({ layoutType }: use.Options): AdPositionElements {
    const [adPositions, setAdPositions] = React.useState(new Array<number>());

    const [adElements, setAdElements] = React.useState(
      new Array<React.ReactNode>()
    );

    const positionData = useSearchPositionInstruction(layoutType);
    const adList = useAdComponents(layoutType, positionData);

    React.useEffect(() => {
      if (!positionData.length) return;
      setAdPositions(positionData.map(({ position }) => position));
    }, [positionData]);

    React.useEffect(() => {
      messageBus.publish(
        events.COMPANION.channel,
        events.COMPANION.LAYOUT_TYPE_CHANGE.topic,
        { layoutType }
      );
    }, [layoutType]);

    /**
     * Make sure returned React component is persisted and memoized
     */
    React.useEffect(() => {
      if (!adList.length) return;

      debug
        .message(
          'adList received. Setting positions and elements from advertising-companion',
          { adList }
        )
        .verbose();

      if (!adPositions.length) {
        const adPositions = adList.map(({ position }) => position);

        debug
          .message(
            'setAdPositions from advertising-companion. Memoized so it will only happen once',
            { adPositions }
          )
          .verbose();

        setAdPositions(adPositions);
      }

      if (!adElements.length) {
        const adElements = adList.map(({ element }) => element);

        debug
          .message(
            'setAdElements from advertising-companion. Memoized so it will only happen once',
            { adElements }
          )
          .verbose();

        setAdElements(adElements);
      }
    }, [adList, setAdPositions, setAdElements]);

    return [adPositions, adElements] as const;
  }

  export namespace use {
    export type Options = {
      readonly layoutType: LayoutType;
    };
  }
}

/**
 * @deprecated Use {@linkcode AdPositionElements.use.Options} instead.
 */
export type UseAdPositionElements = AdPositionElements.use.Options;

/**
 * @deprecated Use {@linkcode AdPositionElements.use} instead.
 */
export const useAdPositionElements = AdPositionElements.use;
